import axios from 'axios'
import axios_client from '../packs/axios_client';
import moment from 'moment';
import _ from 'lodash';

// Export the component definition so it can be imported by both the app and tests
export const EventAutocompleteSearchComponent = {
  data: function () {
    return {
      activeRequestCancelToken: null,
      lastResults: [],
      currentQuery: '',
      pendingResolve: null,
      pendingReject: null,
      isNavigating: false
    }
  },

  created: function() {
    // Create debounced search function once
    this.debouncedSearch = _.debounce(this.performSearch, 500);
    this.pageshowHandler = null;
  },

  mounted: function() {
    // Reset navigation flag when component is mounted (e.g., after browser back button)
    this.resetState();

    // Handle browser back/forward cache (bfcache) - page might be restored without remounting
    // Only handle actual page restoration from cache, not window focus changes
    this.pageshowHandler = (event) => {
      if (event.persisted) {
        // Page was restored from cache (back/forward navigation)
        this.resetState();
        this.$nextTick(() => {
          this.clearInput();
        });
      }
    };
    window.addEventListener('pageshow', this.pageshowHandler);

    // Handle visibility changes to cancel pending operations when window loses focus
    this.visibilityHandler = () => {
      if (document.hidden) {
        // Window lost focus - cancel pending operations to prevent issues
        this.cancelPendingOperations();
      }
    };
    document.addEventListener('visibilitychange', this.visibilityHandler);
  },

  beforeUnmount: function() {
    // Clean up event listeners
    if (this.pageshowHandler) {
      window.removeEventListener('pageshow', this.pageshowHandler);
    }
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
    }

    // Cancel debounced function
    if (this.debouncedSearch) {
      this.debouncedSearch.cancel();
    }

    // Cancel any pending operations
    this.cancelPendingOperations();
  },

  methods: {
    resetState() {
      this.isNavigating = false;
      this.currentQuery = '';
      this.lastResults = [];
      this.cancelPendingOperations();
    },

    cancelPendingOperations() {
      // Cancel any pending promises (resolve with empty array to prevent hanging)
      if (this.pendingResolve) {
        this.pendingResolve([]);
        this.pendingResolve = null;
      }
      if (this.pendingReject) {
        this.pendingReject = null;
      }

      // Cancel any pending requests
      if (this.activeRequestCancelToken) {
        this.activeRequestCancelToken.cancel('Operation cancelled');
        this.activeRequestCancelToken = null;
      }

      // Cancel debounced search
      if (this.debouncedSearch) {
        this.debouncedSearch.cancel();
      }
    },

    clearInput() {
      // Clear the autocomplete input - but don't trigger events that might cause issues
      if (this.$refs.autocomplete) {
        // Try to access the input element
        const autocompleteEl = this.$refs.autocomplete.$el || this.$refs.autocomplete;
        if (autocompleteEl) {
          const input = autocompleteEl.querySelector('input');
          if (input) {
            input.value = '';
            // Don't dispatch events - let Vue handle it naturally
          }
        }
      }
    },
    search(input) {
      if (input.length < 3) {
        // Cancel any pending operations when input is too short
        this.cancelPendingOperations();
        return Promise.resolve([]);
      }

      // Don't proceed if window is not visible (prevents issues when switching windows)
      if (document.hidden) {
        return Promise.resolve([]);
      }

      this.currentQuery = input;

      // Cancel any currently pending request
      if (this.activeRequestCancelToken) {
        this.activeRequestCancelToken.cancel('Subsequent request made');
      }

      // Clear any pending promise callbacks from previous searches
      if (this.pendingResolve) {
        this.pendingResolve([]);
        this.pendingResolve = null;
      }
      if (this.pendingReject) {
        this.pendingReject = null;
      }

      // Generate a cancel token for this request
      this.activeRequestCancelToken = axios.CancelToken.source();

      // Return a promise that will be resolved by the debounced function
      return new Promise((resolve, reject) => {
        this.pendingResolve = resolve;
        this.pendingReject = reject;
        this.debouncedSearch();
      });
    },

    performSearch() {
      // Don't perform search if window is not visible
      if (document.hidden) {
        if (this.pendingResolve) {
          this.pendingResolve([]);
          this.pendingResolve = null;
        }
        if (this.pendingReject) {
          this.pendingReject = null;
        }
        return;
      }

      const params = { 'events': { 'name': this.currentQuery } };
      const token = this.activeRequestCancelToken ? this.activeRequestCancelToken.token : null;
      const resolve = this.pendingResolve;
      const reject = this.pendingReject;

      // Clear pending callbacks immediately to prevent double resolution
      this.pendingResolve = null;
      this.pendingReject = null;

      if (!token) {
        if (resolve) resolve(this.lastResults);
        return;
      }

      axios_client.get(
        '/search/events',
        {
          params: params,
          headers: { 'Accept': 'application/json' },
          cancelToken: token
        }
      ).then(response => {
        // Check if component is still active and window is visible
        if (document.hidden) {
          return;
        }

        this.activeRequestCancelToken = null;
        const highlightedResults = response.data.events.map(event =>
          this.getHighlightedResult(event, this.currentQuery)
        );
        this.lastResults = highlightedResults;
        if (resolve) resolve(highlightedResults);
      }).catch(error => {
        // Check if component is still active
        if (document.hidden) {
          return;
        }

        this.activeRequestCancelToken = null;
        if (axios.isCancel(error)) {
          if (resolve) resolve(this.lastResults);
        } else {
          if (reject) reject(error.message);
        }
      });
    },

    getResultValue(result) {
      // Return the original plain text name (without HTML) for the input field
      return result.originalName || result.name;
    },

    handleSubmit(result) {
      // Prevent multiple rapid navigations
      if (this.isNavigating) return;
      if (!result || !result.slug) return;

      this.isNavigating = true;
      window.location = `/events/${result.slug}`;
    },

    dateRangeText(date1, date2) {
      const moment1 = moment(date1);
      const moment2 = moment(date2);

      if (moment1.isSame(moment2)) {
        return moment1.format('DD MMM YYYY');
      } else if (moment1.year() == moment2.year()) {
        if (moment1.month() == moment2.month()) {
          return `${moment1.date()}-${moment2.date()} ${moment1.format('MMM YYYY')}`;
        } else {
          return `${moment1.format('DD MMM')} - ${moment2.format('DD MMM YYYY')}`;
        }
      }

      return `${moment1.format('DD MMM YYYY')} - ${moment2.format('DD MMM YYYY')}`;
    },

    highlightText(text, query) {
      if (!query || !text) return text;

      // Escape special regex characters
      const escapedQuery = query.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
      const regex = new RegExp(`(${escapedQuery})`, 'gi');

      // Replace matches with highlighted version
      return text.replace(regex, '<mark class="event-autocomplete-highlight">$1</mark>');
    },

    getHighlightedResult(result, query) {
      const name = result.name;
      const location = `${result.city}, ${result.region} (${result.country})`;
      const dateRange = this.dateRangeText(result.start_date, result.end_date);
      const organization = result.organization && result.organization.trim() ? result.organization : '';

      // Only highlight matches in name and organization if query exists
      const highlightedName = query ? this.highlightText(name, query) : name;
      const highlightedOrg = query && organization ? this.highlightText(organization, query) : organization;

      return {
        name: highlightedName,
        originalName: name,
        organization: highlightedOrg,
        location: location,
        dateRange: dateRange,
        slug: result.slug,
        fullText: `${highlightedName} | ${location} | ${dateRange}${highlightedOrg}`
      };
    }
  }
};
